import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import { useAuth } from '../context/AuthContext';
import ChangePasswordModal from './ChangePasswordModal';

const PAGE_TITLES = {
  '/':              'Dashboard',
  '/productos':     'Productos',
  '/sucursales':    'Sucursales',
  '/crear-traslado':'Nuevo Traslado',
  '/traslados':     'Traslados',
};

export default function Topbar({ onMenuClick }) {
  const { pathname } = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const menuRef = useRef(null);

  const title = PAGE_TITLES[pathname]
    || (/^\/sucursales\/\d+\/inventario$/.test(pathname) ? 'Inventario Sucursal' : 'Orígenes');
  const now = new Date().toLocaleDateString('es-VE', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleLogout() {
    setMenuOpen(false);
    logout();
    navigate('/login');
  }

  return (
    <header style={{
      background: 'var(--orig-dark)',
      borderBottom: '1px solid var(--orig-border)',
      height: 'var(--orig-topbar-h)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 24px',
      position: 'sticky', top: 0, zIndex: 50,
    }}>
      {/* Left */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button
          onClick={onMenuClick}
          className="btn-orig btn-orig-outline"
          style={{ padding: '6px 9px', display: 'none' }}
          id="hamburger-btn"
          aria-label="Menú"
        >
          <i className="bi bi-list" style={{ fontSize: 20 }} />
        </button>
        <div>
          <span style={{
            fontSize: 15.5, fontWeight: 700,
            color: 'var(--orig-text)',
            fontFamily: 'var(--orig-font-display)',
            letterSpacing: 0.3,
          }}>
            {title}
          </span>
          <span style={{ fontSize: 11, color: 'var(--orig-text-muted)', marginLeft: 14, textTransform: 'capitalize' }}>
            {now}
          </span>
        </div>
      </div>

      {/* Right */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'var(--orig-panel)',
            border: '1px solid var(--orig-border)',
            borderRadius: 20,
            padding: '4px 9px',
            cursor: 'pointer',
            transition: 'border-color 0.15s',
          }}
        >
          <i
            className="bi bi-sun-fill"
            style={{ fontSize: 13, color: theme === 'light' ? 'var(--orig-amber-light)' : 'var(--orig-text-hint)', transition: 'color 0.2s' }}
          />
          <div style={{
            width: 32, height: 17,
            background: theme === 'light' ? 'var(--orig-green)' : 'var(--orig-border)',
            borderRadius: 10,
            position: 'relative',
            transition: 'background 0.2s',
            flexShrink: 0,
          }}>
            <div style={{
              position: 'absolute',
              top: 2,
              left: theme === 'light' ? 15 : 2,
              width: 13, height: 13,
              background: '#fff',
              borderRadius: '50%',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
            }} />
          </div>
          <i
            className="bi bi-moon-stars-fill"
            style={{ fontSize: 13, color: theme === 'dark' ? 'var(--orig-teal-light)' : 'var(--orig-text-hint)', transition: 'color 0.2s' }}
          />
        </button>

        <button className="btn-orig btn-orig-outline" style={{ padding: '5px 9px' }} title="Notificaciones">
          <i className="bi bi-bell" style={{ fontSize: 16 }} />
        </button>

        {/* User menu */}
        {user && (
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setMenuOpen(prev => !prev)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'transparent',
                border: '1px solid var(--orig-border)',
                borderRadius: 22,
                padding: '4px 10px 4px 4px',
                cursor: 'pointer',
                transition: 'border-color 0.15s, background 0.15s',
                ...(menuOpen ? { borderColor: 'var(--orig-green)', background: 'var(--orig-green-dim)' } : {}),
              }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'var(--orig-green-dim)',
                border: '1px solid rgba(5,150,105,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--orig-green-bright)', fontWeight: 700, fontSize: 11,
                flexShrink: 0,
              }}>
                {user.name?.[0]?.toUpperCase()}
              </div>
              <span style={{
                fontSize: 12.5, fontWeight: 600, color: 'var(--orig-text)',
                maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {user.name}
              </span>
              <i
                className="bi bi-chevron-down"
                style={{
                  fontSize: 10, color: 'var(--orig-text-muted)',
                  transition: 'transform 0.2s',
                  transform: menuOpen ? 'rotate(180deg)' : 'rotate(0)',
                }}
              />
            </button>

            {menuOpen && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                minWidth: 210,
                background: 'var(--orig-dark)',
                border: '1px solid var(--orig-border)',
                borderRadius: 10,
                boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
                padding: '6px 0',
                zIndex: 200,
                animation: 'origFadeIn 0.15s ease-out',
              }}>
                {/* User info header */}
                <div style={{
                  padding: '10px 14px 10px',
                  borderBottom: '1px solid var(--orig-border)',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--orig-text)' }}>
                    {user.name} {user.lastName}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--orig-text-muted)', marginTop: 2 }}>
                    {user.rol}
                  </div>
                </div>

                {/* Menu items */}
                <div style={{ padding: '4px 0' }}>
                  <button
                    onClick={() => { setMenuOpen(false); setShowChangePassword(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%',
                      padding: '9px 14px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 12.5,
                      color: 'var(--orig-text-muted)',
                      transition: 'background 0.12s, color 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--orig-green-dim)'; e.currentTarget.style.color = 'var(--orig-text)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--orig-text-muted)'; }}
                  >
                    <i className="bi bi-key" style={{ fontSize: 14 }} />
                    Cambiar Contraseña
                  </button>
                  <button
                    onClick={handleLogout}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%',
                      padding: '9px 14px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 12.5,
                      color: 'var(--orig-text-muted)',
                      transition: 'background 0.12s, color 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#ef4444'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--orig-text-muted)'; }}
                  >
                    <i className="bi bi-box-arrow-right" style={{ fontSize: 14 }} />
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </header>
  );
}
